// src/components/UserManagement.tsx
import { useMemo, useState, useEffect } from "react";
import { Button, Table, Badge, Form, Modal } from "react-bootstrap";
import DashboardPagesTemplate from "../../Template/DashboardPagesTemplate";
import DashboardPageTitle from "../../Components/InnerPageTitle";
import { useTranslation } from "react-i18next";
import UserModal from "../../Components/UserManagement/UserModal";
import AccessModal from "../../Components/UserManagement/AccessModal";
import DeleteConfirmModal from "../../Components/UserManagement/DeleteConfirmModal";
import { useUserManagement } from "../../hookes/useUserManagement";
import { User as UserType } from "../../PropTypes/UserManagementTypes";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import "../../Assets/scss/components/user-management.scss";
import PaginationComponent from "../../Components/Pagination";
import { useSelector } from "react-redux";

const UserManagement: React.FC = () => {
  const { t } = useTranslation();
  const {
    users,
    allPermission,
    saveUser,
    removeSubUser,
    fetchUserPermissions,
    savePermissionsForUser,
  } = useUserManagement();

  // local UI state
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [usersPerPage, setUsersPerPage] = useState(10);
  const { websiteList, user } = useSelector((store: StoreProptypes) => store);
  // modal state
  const [showUserModal, setShowUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState<UserType | null>(null);

  const [showAccessModal, setShowAccessModal] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserType | null>(null);

  // all websites modal (simple badge list)
  const [showAllWebsitesModal, setShowAllWebsitesModal] = useState(false);
  const [allWebsites, setAllWebsites] = useState<string[]>([]);
  const [allManageAccessWebsites, setManageAccessWebsites] = useState<string[]>(
    [],
  );
  const [disabledButton, setDisabledButton] = useState<boolean>(false);

  // const selectedWebsites = websiteList?.map((i) => allManageAccessWebsites?.filter((val) => console.log("allManageAccessWebsites 2", val === i?.domain)))
  // console.log("allManageAccessWebsites", allManageAccessWebsites, selectedWebsites)
  // Disabled Button Functions
  useEffect(() => {
    // check if admin user
    if (user && user?.department === 1) {
      setDisabledButton(true);
    } else {
      setDisabledButton(false);
    }
  }, [user]);

  // filtered users
  const filteredUsers = useMemo(
    () =>
      users.filter(
        (u) =>
          u.name.toLowerCase().includes(search.toLowerCase()) ||
          u.email.toLowerCase().includes(search.toLowerCase()),
      ),
    [users, search],
  );

  // pagination indices
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);

  const openInviteModal = (user?: UserType) => {
    setEditingUser(user || null);
    setShowUserModal(true);
  };

  const openAccessModal = async (user: UserType) => {
    setEditingUser(user);
    setShowAccessModal(true);
  };

  const confirmDelete = (user: UserType) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!userToDelete) return;
    await removeSubUser(userToDelete.id);
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  return (
    <DashboardPagesTemplate>
      <DashboardPageTitle
        pageTitle={t("User Management")}
        subPageTitle={t("Manage users and their website access")}
      />

      <div className="mt-3 d-flex flex-column flex-md-row gap-2 justify-content-between align-items-md-center">
        {user?.department !== 1 && (
          <Button
            variant="primary"
            disabled={disabledButton}
            onClick={() => openInviteModal()}
          >
            {t("+ Invite User")}
          </Button>
        )}

        <Form.Control
          type="text"
          placeholder={t("Search users...")}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setCurrentPage(1);
          }}
          className="user-management-search w-100 w-md-auto"
        />
      </div>

      <div className="card shadow-sm border-0 rounded-3 mt-3">
        <div className="table-responsive">
          <Table className="mb-0 align-middle user-management-table responsive-table">
            <thead className="bg-light">
              <tr>
                <th>{t("Full Name")}</th>
                <th>{t("Email")}</th>
                <th>{t("Websites")}</th>
                <th className="text-center" style={{ width: 300 }}>
                  {t("Actions")}
                </th>
              </tr>
            </thead>
            <tbody>
              {currentUsers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-4">
                    {t("No matching users")}
                  </td>
                </tr>
              ) : (
                currentUsers.map((u) => (
                  <tr key={u.id}>
                    <td data-label="Full Name">
                      <strong>{u.name}</strong>
                    </td>
                    <td data-label="Email" className="text-muted">
                      {u.email}
                    </td>
                    <td data-label="Websites">
                      {u.website && u.website.length > 0 ? (
                        <div className="website-badges">
                          {u.website.slice(0, 2).map((w: any, idx: number) => (
                            <Badge
                              bg="light"
                              text="dark"
                              className="me-1 border"
                              key={idx}
                            >
                              {w}
                            </Badge>
                          ))}

                          {u.website.length > 2 && (
                            <Badge
                              bg="primary"
                              className="me-1 more-website"
                              style={{ cursor: "pointer" }}
                              onClick={() => {
                                setAllWebsites(u.website || []);
                                setShowAllWebsitesModal(true);
                              }}
                            >
                              {`+${u.website.length - 2}`}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-muted">{t("No access")}</span>
                      )}
                    </td>

                    <td data-label="Actions" className="text-center">
                      <div>
                      {user?.department !== 1 && (
                        <Button
                          variant="outline-light text-primary"
                          size="sm"
                          className="me-2 action-btn"
                          title="Edit"
                          onClick={() => openInviteModal(u)}
                        >
                          <i className="material-symbols-outlined">
                            {t("edit")}
                          </i>
                        </Button>
                      )}

                      <Button
                        variant="outline-light text-primary"
                        size="sm"
                        className="me-2 action-btn"
                        title="Access"
                        onClick={() => {
                          setManageAccessWebsites(u?.website || []);
                          openAccessModal(u);
                        }}
                      >
                        <i className="material-symbols-outlined">
                          {t("settings")}
                        </i>
                      </Button>
                      {user?.department !== 1 && (
                        <Button
                          variant="outline-light text-danger"
                          size="sm"
                          className="action-btn"
                          title="Delete"
                          onClick={() => confirmDelete(u)}
                        >
                          <i className="material-symbols-outlined">
                            {t("delete")}
                          </i>
                        </Button>
                      )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top bg-light">
          {/* Showing X - Y of Z Users */}
          <small className="text-muted">
            {t("Showing")} {indexOfFirstUser + 1} -{" "}
            {Math.min(indexOfLastUser, filteredUsers.length)} {t("of")}{" "}
            {filteredUsers.length} {t("user(s)")}
          </small>

          <div className="d-flex align-items-center">
            {/* Page Size Selector */}
            <Form.Select
              value={usersPerPage}
              onChange={(e) => {
                setUsersPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              style={{ width: "63px" }}
              size="sm"
              className="me-2"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
            </Form.Select>

            {/* Use Common Pagination */}
            <PaginationComponent
              currentPage={currentPage}
              totalRecords={filteredUsers.length}
              itemsPerPage={usersPerPage}
              setCurrentPage={setCurrentPage}
            />
          </div>
        </div>
      </div>

      <UserModal
        show={showUserModal}
        onHide={() => setShowUserModal(false)}
        websiteList={websiteList} // NOTE: pass actual websiteList from selector if you keep it in hook or redux
        allPermission={allPermission} // NOTE: pass actual allPermission from hook
        editingUser={editingUser}
        onSave={async (payload, editingUserId, websites) => {
          // map websites param and forward to saveUser hook
          return saveUser(payload, editingUserId, websites as any);
        }}
      />

      <AccessModal
        show={showAccessModal}
        onHide={() => setShowAccessModal(false)}
        websiteList={websiteList} // populate actual website list
        allPermission={allPermission} // populate actual permission list
        editingUser={editingUser || undefined}
        disabledButton={disabledButton}
        fetchUserPermissions={fetchUserPermissions}
        onSaveAccess={savePermissionsForUser}
        allManageAccessWebsites={allManageAccessWebsites}
      />

      <DeleteConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        user={userToDelete || undefined}
        onConfirm={handleDeleteConfirmed}
      />

      {/* All Websites Modal */}
      <ModalAllWebsites
        show={showAllWebsitesModal}
        onHide={() => setShowAllWebsitesModal(false)}
        websites={allWebsites}
      />
    </DashboardPagesTemplate>
  );
};
export default UserManagement;

// Small inline component for modal showing all websites
type MAProps = { show: boolean; onHide: () => void; websites: string[] };
const ModalAllWebsites: React.FC<MAProps> = ({ show, onHide, websites }) => {
  const { t } = useTranslation();
  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{t("All Websites")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {websites.map((w, idx) => (
          <Badge
            bg="light"
            text="dark"
            className="me-1 mb-1 border custom-badge"
            key={idx}
          >
            {w}
          </Badge>
        ))}
      </Modal.Body>
    </Modal>
  );
};
